import React, { useState, useEffect } from "react";
import { Drawer, List, ListItem, ListItemText, Collapse, IconButton, AppBar, Toolbar } from "@mui/material";
import { ExpandMore, ExpandLess, Menu as MenuIcon, AccountCircle, Close, Dashboard } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import CategoryIcon from "@mui/icons-material/Category";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import PersonIcon from "@mui/icons-material/Person";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import Marquee from "react-fast-marquee";

import { HeaderContain, HeaderTitle, SideBarHeadContain, SideBarList } from "../../style/header/Header";

import { useMatches } from "../../style/theme";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../features/DrawerSlice";
import { setRole } from "../../features/RoleSlice";

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { matches } = useMatches();
  const location = useLocation();

  const drawer = useSelector((state) => state.drawer);
  const [drawerOpen, setDrawerOpen] = useState(() => !matches);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [routeChanged, setRouteChanged] = useState(false);

  const [userCred, setUserCred] = useState(() => JSON.parse(localStorage.getItem("user_cred")));

  const autoCloseRoutes = ["/exam", "/course/"];

  useEffect(() => {
    const storedCred = JSON.parse(localStorage.getItem("user_cred"));
    setUserCred(storedCred);
  }, [location.pathname]);

  useEffect(() => {
    setRouteChanged(true);

    const shouldAutoClose = autoCloseRoutes.some((route) => location.pathname.includes(route) || location.pathname === route);

    if (shouldAutoClose && drawerOpen) {
      setDrawerOpen(false);
    }

    const timer = setTimeout(() => {
      setRouteChanged(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const currentRole = userCred?.roleType || "user";
  const userName = userCred?.userName || "Manthan-It-Solutions";
  const userId = userCred?.userId || "";

  useEffect(() => {
    if (!routeChanged) {
      setDrawerOpen(!matches);
    }
  }, [matches]);

  useEffect(() => {
    if (matches) {
      dispatch(setDrawer(true));
    } else {
      dispatch(setDrawer(!drawerOpen));
    }
  }, [drawerOpen, dispatch, matches]);

  const handleToggle = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  // Toggle drawer open/close manually
  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleLogOut = (e) => {
    e.preventDefault();
    localStorage.clear();
    dispatch(setRole(null));
    navigate(currentRole === "admin" ? "/admin-login" : "/");
  };

  //  ADMIN Sidebar Navigation
  const adminNavLinks = [
    { label: "Dashboard", link: "/admin-dashboard", icon: <DashboardIcon /> },
    {
      label: "Manage Course",
      link: "#",
      icon: <AppRegistrationIcon />,
      sublinks: [
        { label: "Add Course", link: "/add-course", icon: <CreateNewFolderIcon /> },
        { label: "Course Category", link: "/course-category", icon: <CategoryIcon /> },
      ],
    },
    {
      label: "Manage Users",
      link: "#",
      icon: <ManageAccountsIcon />,
      sublinks: [
        { label: "Add Users", link: "/add-user", icon: <PersonIcon /> },
        { label: "User List", link: "/user-list", icon: <RecentActorsIcon /> },
        { label: "Manage Dashboard", link: "/manage-dashboard", icon: <Dashboard /> },

      ],
    },
  
    {
      label: "Suggestions",
      link: "#",
     icon: <LightbulbOutlinedIcon/> ,
      sublinks: [
        { label: "Add Suggestion", link: "/suggestion", icon:<FeedbackIcon />  },
        { label: "Suggestion List", link: "/suggestionList", icon: <QueryStatsIcon /> },
      ],
    },


    { label: "Support", link: "/admin-ticket-dashboard", icon: <SupportAgentOutlinedIcon /> },
    { label: "Log Out", link: "/", icon: <LogoutOutlinedIcon /> },
  ];

  // USER Sidebar Navigation
  const userNavLinks = [
    { label: "Dashboard", link: "/dashboard", icon: <DashboardIcon /> },
    { label: "View Courses", link: "/view-courses/0", icon: <CreateNewFolderIcon /> },
    { label: "My Courses", link: "/course", icon: <CreateNewFolderIcon /> },
    { label: "Support", link: "/support", icon: <SupportAgentOutlinedIcon /> },
    { label: "Log Out", link: "/", icon: <LogoutOutlinedIcon /> },
  ];

  const navLinks = currentRole === "admin" ? adminNavLinks : userNavLinks;
  const headerTitle = "Learning Management System";

  return (
    <HeaderContain className="HeaderContain">
      <AppBar position="fixed" sx={{ backgroundColor: "white" }} className={`Appbar ${drawer ? "" : "DrawerOpen"}`}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer} className={`menuButton ${drawer ? "" : "MenuButton"}`}>
            <MenuIcon />
          </IconButton>
          <HeaderTitle variant="h6">
            <Marquee gradient={false} speed={30}>
              {headerTitle}
            </Marquee>
          </HeaderTitle>
          <div></div>
        </Toolbar>
      </AppBar>

      <Drawer className="Drawer" variant="persistent" anchor="left" open={drawerOpen}>
        <SideBarHeadContain>
          <div className="SideBar">
            <Link to="/profile">
              <IconButton className="icon">
                <AccountCircle />
              </IconButton>
            </Link>
            <div className="user_name_text">
              <span>{userName}</span>
              <span>{currentRole}</span>
              <span>{userId}</span>
            </div>
          </div>
          <IconButton onClick={() => setDrawerOpen(false)} className="icon">
            <Close />
          </IconButton>
        </SideBarHeadContain>

        <SideBarList className="SidebarList">
          {navLinks.map((link, index) => (
            <React.Fragment key={index}>
              {!link.sublinks ? (
                <ListItem className="Border_top">
                  <Link to={link.link} onClick={(e) => (link.label === "Log Out" ? handleLogOut(e) : null)} className="LinkSideBar linkStyle">
                    {link.icon}
                    <ListItemText primary={link.label} />
                  </Link>
                </ListItem>
              ) : (
                <>
                  <ListItem onClick={() => handleToggle(link.label)} className="LinkSideBar Border_top">
                    {link.icon}
                    <ListItemText primary={link.label} />
                    <IconButton>{openDropdown === link.label ? <ExpandLess /> : <ExpandMore />}</IconButton>
                  </ListItem>
                  <Collapse in={openDropdown === link.label} timeout="auto" unmountOnExit className="ContainDropDown">
                    <List disablePadding className="DropDown">
                      {link.sublinks.map((sublink, subIndex) => (
                        <ListItem key={`sub-${index}-${subIndex}`} sx={{ paddingLeft: 4 }}>
                          <Link to={sublink.link} className="LinkSideBar linkStyle">
                            {sublink.icon}
                            <ListItemText primary={sublink.label} />
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}
            </React.Fragment>
          ))}
        </SideBarList>
      </Drawer>
    </HeaderContain>
  );
};

export default Sidebar;